/**
 * Presence hooks — publish + observe peers' cursor + viewport on the
 * workspace-scoped Y.Awareness (Plan v3-01, todo `presence`).
 *
 * The workspace room is mounted by `CanvasYjsProvider` and the
 * `presence` awareness key is reserved (pod-enforced).
 *
 * Performance contract (per plan): emit ≤30Hz, render ≤60Hz; remote
 * peers fade out after PRESENCE_STALE_AFTER_MS.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Awareness } from 'y-protocols/awareness';

import { useCanvasYjs } from '@/components/workspace/canvas/yjs-mount';
import {
  PRESENCE_AWARENESS_KEY,
  PRESENCE_CURSOR_EMIT_INTERVAL_MS,
  PRESENCE_STALE_AFTER_MS,
  type Participant,
  type PresencePayload,
  type PresenceUser,
} from './types';

interface AwarenessState {
  user?: PresenceUser;
  presence?: PresencePayload;
}

export interface RemotePeer {
  /**
   * The Yjs awareness client id — unique per live connection by construction.
   * Use this (not `user.peerId`, which is peer-published and can be missing or
   * duplicated across tabs / reconnects) as the React key for peer nodes.
   */
  clientId: number;
  user: PresenceUser;
  payload: PresencePayload;
}

/** Subscribe to other peers' presence state on the workspace room. */
export function useRemotePresence(): RemotePeer[] {
  const { workspaceRoom } = useCanvasYjs();
  const [peers, setPeers] = useState<RemotePeer[]>([]);

  useEffect(() => {
    const awareness = workspaceRoom.awareness;
    if (!awareness) return;

    let raf: number | null = null;
    const localId = awareness.clientID;

    const computePeers = (): RemotePeer[] => {
      const now = Date.now();
      const out: RemotePeer[] = [];
      awareness.getStates().forEach((stateRaw, clientId) => {
        if (clientId === localId) return;
        const state = stateRaw as AwarenessState;
        if (!state.user) return;
        const payload = state.presence ?? {};
        if (payload.cursor && now - payload.cursor.ts > PRESENCE_STALE_AFTER_MS) {
          // Cursor stale — keep the peer (they may still own a viewport)
          // but strip the cursor so it stops rendering.
          payload.cursor = undefined;
        }
        out.push({ clientId, user: state.user, payload });
      });
      return out;
    };

    const handler = () => {
      // requestAnimationFrame throttles re-renders to display refresh
      // (~60Hz on most screens, satisfies the plan's 60Hz render cap).
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        setPeers(computePeers());
      });
    };

    awareness.on('change', handler);
    handler();

    return () => {
      awareness.off('change', handler);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [workspaceRoom.awareness]);

  return peers;
}

/**
 * Stable remote-participant list (identity + focused app), excluding
 * self. Re-renders ONLY when membership or focus changes — never on the
 * 60Hz cursor churn — so it's safe to drive the rail badge + the
 * Collaborate panel off it. (Contrast `useRemotePresence`, which
 * updates every frame for the cursor overlay.)
 */
export function useParticipants(): Participant[] {
  const { workspaceRoom } = useCanvasYjs();
  const [list, setList] = useState<Participant[]>([]);

  useEffect(() => {
    const awareness = workspaceRoom.awareness;
    if (!awareness) return;
    const localId = awareness.clientID;
    const sig = (ps: Participant[]) =>
      ps
        .map(
          (p) =>
            `${p.user.peerId}:${p.focusItemId ?? ''}:${p.screenItemId ?? ''}`,
        )
        .join('|');
    const compute = () => {
      const next: Participant[] = [];
      awareness.getStates().forEach((stateRaw, clientId) => {
        if (clientId === localId) return;
        const state = stateRaw as AwarenessState;
        if (!state.user) return;
        next.push({
          user: state.user,
          focusItemId: state.presence?.focus?.itemId ?? null,
          screenItemId: state.presence?.screen?.itemId ?? null,
        });
      });
      // Bail on no-op so cursor moves (which also fire 'change') don't
      // re-render every consumer.
      setList((prev) => (sig(prev) === sig(next) ? prev : next));
    };
    awareness.on('change', compute);
    compute();
    return () => awareness.off('change', compute);
  }, [workspaceRoom.awareness]);

  return list;
}

/**
 * Whether any OTHER peer is connected to the workspace room. Cheap to
 * subscribe to: re-renders only when the boolean flips (peer joins /
 * leaves), NOT on every cursor move — so it's safe to gate the
 * high-frequency publishers on it (don't broadcast a cursor when
 * you're the only one in the room).
 */
export function useHasRemotePeers(): boolean {
  const { workspaceRoom } = useCanvasYjs();
  const [has, setHas] = useState(false);

  useEffect(() => {
    const awareness = workspaceRoom.awareness;
    if (!awareness) return;
    const localId = awareness.clientID;
    const compute = () => {
      let any = false;
      awareness.getStates().forEach((stateRaw, clientId) => {
        if (clientId !== localId && (stateRaw as AwarenessState).user) any = true;
      });
      // setState bails out when the value is unchanged → no re-render
      // on the 60Hz cursor churn, only when a peer actually appears.
      setHas(any);
    };
    awareness.on('change', compute);
    compute();
    return () => awareness.off('change', compute);
  }, [workspaceRoom.awareness]);

  return has;
}

/**
 * Publish which app (workspace item) the local user is focused on.
 * Emits only on change (the value is low-frequency — selection /
 * maximize), not on a timer.
 */
export function usePublishLocalFocus(): (itemId: string | null) => void {
  const { workspaceRoom } = useCanvasYjs();
  const lastRef = useRef<string | null | undefined>(undefined);

  return useCallback(
    (itemId: string | null) => {
      const awareness = workspaceRoom.awareness as Awareness | undefined;
      if (!awareness) return;
      if (lastRef.current === itemId) return;
      lastRef.current = itemId;
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      const presence: PresencePayload = { ...(current.presence ?? {}) };
      presence.focus = { itemId };
      awareness.setLocalStateField(PRESENCE_AWARENESS_KEY, presence);
    },
    [workspaceRoom.awareness],
  );
}

/**
 * Publish which SCREEN (window-management v2) the local camera is
 * parked on. Same emit-on-change shape as `usePublishLocalFocus` —
 * screen changes are navigation-frequency, not per-frame.
 */
export function usePublishLocalScreen(): (itemId: string | null) => void {
  const { workspaceRoom } = useCanvasYjs();
  const lastRef = useRef<string | null | undefined>(undefined);

  return useCallback(
    (itemId: string | null) => {
      const awareness = workspaceRoom.awareness as Awareness | undefined;
      if (!awareness) return;
      if (lastRef.current === itemId) return;
      lastRef.current = itemId;
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      const presence: PresencePayload = { ...(current.presence ?? {}) };
      presence.screen = { itemId };
      awareness.setLocalStateField(PRESENCE_AWARENESS_KEY, presence);
    },
    [workspaceRoom.awareness],
  );
}

/**
 * Publish the local cursor to the workspace awareness. The caller
 * (typically `<PresenceCursorPublisher>`) converts screen→canvas
 * coordinates via ReactFlow.
 */
export function usePublishLocalCursor(): (canvasX: number, canvasY: number) => void {
  const { workspaceRoom } = useCanvasYjs();
  const lastEmitRef = useRef(0);

  return useCallback(
    (canvasX: number, canvasY: number) => {
      const awareness = workspaceRoom.awareness as Awareness | undefined;
      if (!awareness) return;
      const now = Date.now();
      if (now - lastEmitRef.current < PRESENCE_CURSOR_EMIT_INTERVAL_MS) return;
      lastEmitRef.current = now;
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      const presence: PresencePayload = { ...(current.presence ?? {}) };
      presence.cursor = { x: canvasX, y: canvasY, ts: now };
      awareness.setLocalStateField(PRESENCE_AWARENESS_KEY, presence);
    },
    [workspaceRoom.awareness],
  );
}

/** Publish viewport rectangle (in canvas coordinates) — emit on debounce. */
export function usePublishLocalViewport(): (rect: { x: number; y: number; width: number; height: number }) => void {
  const { workspaceRoom } = useCanvasYjs();
  const lastEmitRef = useRef(0);

  return useCallback(
    (rect) => {
      const awareness = workspaceRoom.awareness as Awareness | undefined;
      if (!awareness) return;
      const now = Date.now();
      // 100ms debounce — viewport doesn't need 30Hz.
      if (now - lastEmitRef.current < 100) return;
      lastEmitRef.current = now;
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      const presence: PresencePayload = { ...(current.presence ?? {}) };
      presence.viewport = rect;
      awareness.setLocalStateField(PRESENCE_AWARENESS_KEY, presence);
    },
    [workspaceRoom.awareness],
  );
}

/**
 * Follow-me: when set, ReactFlow viewport snaps to the target peer's
 * viewport until the local user scrolls/zooms. Returns the target
 * peer's current viewport (null when no one is being followed).
 */
export function useFollowMe(): {
  followingPeerId: string | null;
  follow: (peerId: string | null) => void;
  targetViewport: PresencePayload['viewport'] | null;
} {
  const { workspaceRoom } = useCanvasYjs();
  const [followingPeerId, setFollowingPeerId] = useState<string | null>(null);
  const peers = useRemotePresence();

  const follow = useCallback(
    (peerId: string | null) => {
      setFollowingPeerId(peerId);
      const awareness = workspaceRoom.awareness as Awareness | undefined;
      if (!awareness) return;
      const current = (awareness.getLocalState() ?? {}) as AwarenessState;
      const presence: PresencePayload = { ...(current.presence ?? {}) };
      presence.follow = { followingPeerId: peerId };
      awareness.setLocalStateField(PRESENCE_AWARENESS_KEY, presence);
    },
    [workspaceRoom.awareness],
  );

  const targetViewport = followingPeerId
    ? peers.find((p) => p.user.peerId === followingPeerId)?.payload.viewport ?? null
    : null;

  return { followingPeerId, follow, targetViewport };
}
